/**
 * Community invite API for the public invite landing page and signed-in accept flow.
 * GET /community/connection-invites/{inviteId} — no auth required (links sent via SMS/email).
 * POST claim + PUT ACCEPTED — authenticated (iOS InviteAcceptanceView parity).
 * GET list with inviterUserId=me — owner sent-invites roster (iOS TripSharingService).
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';
import {
  connectionInviteMatchesTrip,
  connectionInvitePrimaryLabel,
} from '../utils/tripShareInviteMatch';

export type InviteStatus = 'pending' | 'accepted' | 'expired';

export interface InviteDetails {
  inviteId: string;
  inviterName: string;
  inviterEmail?: string;
  status: InviteStatus;
}

export type InviteFetchResult =
  | { ok: true; invite: InviteDetails }
  | { ok: false; reason: 'not_found' | 'expired' | 'network' | 'unknown' };

export interface AcceptConnectionInviteResult {
  inviteId: string;
  status: string;
  tripId: string | null;
  alreadyAccepted: boolean;
}

export interface CreatedConnectionInvite {
  inviteId: string;
  invitationLink: string;
  status: string;
}

/** Row from GET /community/connection-invites (iOS CommunityAPI.APIConnectionInvite). */
export interface ConnectionInvite {
  id: string;
  userId: string;
  inviteeUserId: string;
  tripId: string | null;
  role: string | null;
  message: string | null;
  status: string;
  expiresAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export class InviteAcceptError extends Error {
  readonly statusCode: number;

  constructor(statusCode: number, message: string) {
    super(message);
    this.name = 'InviteAcceptError';
    this.statusCode = statusCode;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Fetch invite details for the landing page. Returns null-like result on failure
 * so the page can show generic copy without breaking.
 */
export async function fetchInviteDetails(inviteId: string): Promise<InviteFetchResult> {
  const url = `${API_BASE_URL}/community/connection-invites/${encodeURIComponent(inviteId)}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.status === 404) {
      return { ok: false, reason: 'not_found' };
    }

    if (!response.ok) {
      return { ok: false, reason: 'unknown' };
    }

    const data = (await response.json()) as Partial<InviteDetails> & {
      status?: string;
    };

    const rawStatus = String(data.status ?? 'pending').toLowerCase();
    if (rawStatus === 'expired') {
      return { ok: false, reason: 'expired' };
    }

    const inviterName = data.inviterName?.trim();
    if (!inviterName) {
      return { ok: false, reason: 'unknown' };
    }

    const status: InviteStatus =
      rawStatus === 'accepted' ? 'accepted' : 'pending';

    return {
      ok: true,
      invite: {
        inviteId: data.inviteId ?? inviteId,
        inviterName,
        inviterEmail: data.inviterEmail,
        status,
      },
    };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/**
 * Attach a pending email/SMS invite to the signed-in user (does not accept).
 * POST /community/connection-invites/claim
 */
export async function claimConnectionInvite(params: {
  inviteId: string;
  email: string;
}): Promise<void> {
  const inviteId = params.inviteId.trim();
  const email = params.email.trim();
  if (!inviteId || !email) {
    throw new InviteAcceptError(400, 'inviteId and email are required');
  }

  const headers = await buildAuthHeaders(true);
  const res = await fetch(`${API_BASE_URL}/community/connection-invites/claim`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ inviteId, email }),
  });

  if (!res.ok) {
    throw new InviteAcceptError(
      res.status,
      `Failed to claim invite (${res.status})`
    );
  }
}

/**
 * Accept a claimed connection invite.
 * PUT /community/connection-invites/{id} { status: "ACCEPTED" }
 */
export async function acceptConnectionInvite(
  inviteId: string
): Promise<AcceptConnectionInviteResult> {
  const id = inviteId.trim();
  if (!id) {
    throw new InviteAcceptError(400, 'inviteId is required');
  }

  const headers = await buildAuthHeaders(true);
  const res = await fetch(
    `${API_BASE_URL}/community/connection-invites/${encodeURIComponent(id)}`,
    {
      method: 'PUT',
      headers,
      body: JSON.stringify({ status: 'ACCEPTED' }),
    }
  );

  if (res.status === 409) {
    return {
      inviteId: id,
      status: 'ACCEPTED',
      tripId: null,
      alreadyAccepted: true,
    };
  }

  if (!res.ok) {
    // Some backends return 400/404 once already accepted — treat as already accepted when body hints.
    if (res.status === 400 || res.status === 404) {
      let bodyText = '';
      try {
        bodyText = (await res.text()).toLowerCase();
      } catch {
        bodyText = '';
      }
      if (
        bodyText.includes('accepted') ||
        bodyText.includes('already') ||
        bodyText.includes('not pending')
      ) {
        return {
          inviteId: id,
          status: 'ACCEPTED',
          tripId: null,
          alreadyAccepted: true,
        };
      }
    }
    throw new InviteAcceptError(
      res.status,
      `Failed to accept invite (${res.status})`
    );
  }

  let tripId: string | null = null;
  let status = 'ACCEPTED';
  try {
    const json = asRecord(await res.json());
    const rawTrip = json.tripId ?? json.trip_id;
    tripId = rawTrip != null && String(rawTrip).trim() ? String(rawTrip) : null;
    if (json.status != null) status = String(json.status);
  } catch {
    // empty / non-JSON body is fine
  }

  return {
    inviteId: id,
    status,
    tripId,
    alreadyAccepted: false,
  };
}

/** Digits-only phone key for SMS invites — mirrors iOS PhoneNumberFormatting. */
export function phonePlaceholderInviteeEmail(rawPhone: string): string | null {
  const digits = rawPhone.replace(/\D/g, '');
  if (digits.length < 8) return null;
  return `phone_${digits}@wanderertrip.placeholder`;
}

function invitationLinkForId(inviteId: string): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/invite/${encodeURIComponent(inviteId)}`;
  }
  return `/invite/${encodeURIComponent(inviteId)}`;
}

/**
 * Create a pending connection invite (email or SMS placeholder).
 * POST /community/connection-invites — inviteeUserId is normalized email or phone_* placeholder.
 */
export async function createConnectionInvite(params: {
  inviteeEmailOrPhoneKey: string;
  tripId?: string | null;
  role?: string | null;
  message?: string | null;
}): Promise<CreatedConnectionInvite> {
  const inviteeUserId = params.inviteeEmailOrPhoneKey.trim().toLowerCase();
  if (!inviteeUserId) {
    throw new InviteAcceptError(400, 'invitee is required');
  }

  const headers = await buildAuthHeaders(true);
  const body: Record<string, unknown> = { inviteeUserId };
  const tripId = params.tripId?.trim();
  if (tripId) body.tripId = tripId.toLowerCase();
  if (params.role?.trim()) body.role = params.role.trim();
  if (params.message?.trim()) body.message = params.message.trim();

  const res = await fetch(`${API_BASE_URL}/community/connection-invites`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new InviteAcceptError(
      res.status,
      `Failed to create invite (${res.status})`
    );
  }

  let inviteId = '';
  let status = 'PENDING';
  try {
    const json = asRecord(await res.json());
    inviteId = String(json.id ?? json.inviteId ?? json.invite_id ?? '').trim();
    if (json.status != null) status = String(json.status);
  } catch {
    // empty body
  }
  if (!inviteId) {
    throw new InviteAcceptError(500, 'Invite created without id');
  }

  return {
    inviteId,
    invitationLink: invitationLinkForId(inviteId),
    status,
  };
}

export async function createConnectionInviteByEmail(params: {
  email: string;
  tripId?: string | null;
  role?: string | null;
  message?: string | null;
}): Promise<CreatedConnectionInvite> {
  const email = params.email.trim().toLowerCase();
  if (!email || !email.includes('@')) {
    throw new InviteAcceptError(400, 'valid email is required');
  }
  return createConnectionInvite({
    inviteeEmailOrPhoneKey: email,
    tripId: params.tripId,
    role: params.role,
    message: params.message,
  });
}

export async function createConnectionInviteByPhone(params: {
  phone: string;
  tripId?: string | null;
  role?: string | null;
  message?: string | null;
}): Promise<CreatedConnectionInvite> {
  const key = phonePlaceholderInviteeEmail(params.phone);
  if (!key) {
    throw new InviteAcceptError(400, 'valid phone is required');
  }
  return createConnectionInvite({
    inviteeEmailOrPhoneKey: key,
    tripId: params.tripId,
    role: params.role,
    message: params.message,
  });
}

export function parseConnectionInvite(raw: unknown): ConnectionInvite {
  const o = asRecord(raw);
  return {
    id: String(o.id ?? o.inviteId ?? o.invite_id ?? '').trim(),
    userId: String(o.userId ?? o.user_id ?? '').trim(),
    inviteeUserId: String(o.inviteeUserId ?? o.invitee_user_id ?? '').trim(),
    tripId: (() => {
      const v = o.tripId ?? o.trip_id;
      if (v == null) return null;
      const s = String(v).trim();
      return s.length > 0 ? s : null;
    })(),
    role: (() => {
      const v = o.role;
      if (v == null) return null;
      const s = String(v).trim();
      return s.length > 0 ? s : null;
    })(),
    message: (() => {
      const v = o.message;
      if (v == null) return null;
      const s = String(v).trim();
      return s.length > 0 ? s : null;
    })(),
    status: String(o.status ?? '').trim(),
    expiresAt: o.expiresAt != null ? String(o.expiresAt) : o.expires_at != null ? String(o.expires_at) : null,
    createdAt: o.createdAt != null ? String(o.createdAt) : o.created_at != null ? String(o.created_at) : null,
    updatedAt: o.updatedAt != null ? String(o.updatedAt) : o.updated_at != null ? String(o.updated_at) : null,
  };
}

/**
 * List connection invites. Pass `inviterUserId: 'me'` for invites the current user sent
 * (iOS TripSharingService.loadSentConnectionInvites).
 */
export async function listConnectionInvites(params?: {
  inviteeUserId?: string | null;
  inviterUserId?: string | null;
  cursor?: string | null;
  limit?: number;
}): Promise<ConnectionInvite[]> {
  const headers = await buildAuthHeaders();
  const qs = new URLSearchParams();
  if (params?.inviteeUserId != null && params.inviteeUserId !== '') {
    qs.set('inviteeUserId', params.inviteeUserId);
  }
  if (params?.inviterUserId != null && params.inviterUserId !== '') {
    qs.set('inviterUserId', params.inviterUserId);
  }
  if (params?.cursor) qs.set('cursor', params.cursor);
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const url = `${API_BASE_URL}/community/connection-invites${query ? `?${query}` : ''}`;

  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    throw new InviteAcceptError(res.status, `Failed to list connection invites (${res.status})`);
  }

  const json = await res.json();
  const rows = Array.isArray(json)
    ? json
    : Array.isArray(json?.invites)
      ? json.invites
      : Array.isArray(json?.items)
        ? json.items
        : [];
  return rows.map(parseConnectionInvite).filter((r: ConnectionInvite) => !!r.id);
}

/**
 * Pending connection invites the current user sent for this trip.
 * Soft-fails to `[]` if the server rejects the list (iOS loadSentConnectionInvites).
 */
export async function listPendingSentInvitesForTrip(tripId: string): Promise<ConnectionInvite[]> {
  try {
    const items = await listConnectionInvites({
      inviteeUserId: null,
      inviterUserId: 'me',
      limit: 1000,
    });
    return items.filter(
      (invite) =>
        invite.status.toUpperCase() === 'PENDING' &&
        connectionInviteMatchesTrip(invite.tripId, tripId)
    );
  } catch {
    return [];
  }
}

export { connectionInvitePrimaryLabel };
